import { useState, useEffect, useRef, useCallback } from "react";

const INTERVAL = 3000;

export function ImageCarousel({ images = [] }) {
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  const goTo = useCallback(
    (index) => {
      setCurrent((index + images.length) % images.length);
      setProgress(0);
      startRef.current = performance.now();
    },
    [images.length],
  );

  const animateProgress = useCallback((ts) => {
    const elapsed = ts - (startRef.current ?? ts);
    const pct = Math.min((elapsed / INTERVAL) * 100, 100);
    setProgress(pct);
    if (pct < 100) rafRef.current = requestAnimationFrame(animateProgress);
  }, []);

  useEffect(() => {
    startRef.current = performance.now();
    rafRef.current = requestAnimationFrame(animateProgress);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
      setProgress(0);
      startRef.current = performance.now();
    }, INTERVAL);

    return () => {
      clearInterval(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [animateProgress, images.length]);

  const handleManual = (index) => {
    clearInterval(timerRef.current);
    cancelAnimationFrame(rafRef.current);
    goTo(index);
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
      setProgress(0);
      startRef.current = performance.now();
    }, INTERVAL);
    rafRef.current = requestAnimationFrame(animateProgress);
  };

  if (!images.length) return null;

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-gray-100">
        {images.map((img, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-700 ease-in-out"
            style={{ opacity: i === current ? 1 : 0 }}
          >
            <img
              src={img.src}
              alt={img.alt ?? `Slide ${i + 1}`}
              className="w-full h-full object-contain"
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}

        {/* Flechas */}
        <button
          onClick={() => handleManual(current - 1)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/35 text-white flex items-center justify-center hover:bg-black/55 transition-colors"
        >
          ‹
        </button>
        <button
          onClick={() => handleManual(current + 1)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/35 text-white flex items-center justify-center hover:bg-black/55 transition-colors"
        >
          ›
        </button>

        {/* Contador */}
        <span className="absolute top-2.5 right-3 text-xs font-medium text-white/90 bg-black/30 px-2 py-0.5 rounded-full">
          {current + 1} / {images.length}
        </span>

        {/* Barra de progreso */}
        <div
          className="absolute bottom-0 left-0 h-0.5 bg-white/80 rounded-r"
          style={{ width: `${progress}%`, transition: "none" }}
        />
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-3">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => handleManual(i)}
            className="h-1.5 rounded-full transition-all duration-300 bg-gray-400"
            style={{
              width: i === current ? 20 : 6,
              opacity: i === current ? 1 : 0.5,
            }}
            aria-label={`Ir a imagen ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
